import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../supabaseClient'
import { C, st, formatDate } from '../../theme'
import { PageHeader, EmptyState, Modal } from '../../components/UI'
import { useAppPrefs } from '../../AppPrefs'
import { makeT } from '../../i18n'

// ── Constants ─────────────────────────────────────────────────────────────────
const FIVE_S = [
  {
    key: 'seiri', jp: '整理', zh: '清除不必要物品', en: 'Sort', icon: '🗂️',
    color: '#ef4444', bg: '#fee2e2',
    desc: '工作區域內只保留必要的東西，多餘物品移除',
    items: ['工作區沒有不需要的物品', '過期物料已清除', '不用的工具已歸還或報廢'],
  },
  {
    key: 'seiton', jp: '整頓', zh: '物品定位放置', en: 'Set in Order', icon: '📐',
    color: '#f97316', bg: '#ffedd5',
    desc: '每樣東西都有固定位置，用完放回原處',
    items: ['工具有固定放置位置', '物品擺放整齊有標示', '通道保持暢通'],
  },
  {
    key: 'seiso', jp: '清掃', zh: '保持工作環境清潔', en: 'Shine', icon: '🧹',
    color: '#eab308', bg: '#fef9c3',
    desc: '每天清掃工作區域，設備保持清潔',
    items: ['工作台面乾淨整潔', '機台設備定期擦拭', '地板無油污垃圾'],
  },
  {
    key: 'seiketsu', jp: '清潔', zh: '維持清潔標準', en: 'Standardize', icon: '✅',
    color: '#22c55e', bg: '#dcfce7',
    desc: '建立標準讓前三個S持續執行',
    items: ['有明確的清潔流程', '定期檢查並記錄', '新進員工有5S說明'],
  },
  {
    key: 'shitsuke', jp: '素養', zh: '養成良好習慣', en: 'Sustain', icon: '🌟',
    color: '#3b82f6', bg: '#dbeafe',
    desc: '每個人自律遵守5S規定，形成習慣',
    items: ['員工主動維持5S', '發現問題立即改善', '5S已成為工作習慣'],
  },
]

const SCORE_OPTIONS = [
  { v: 1, label: '不合格', en: 'Unacceptable', color: '#ef4444', bg: '#fee2e2' },
  { v: 2, label: '待改善', en: 'Poor',         color: '#f97316', bg: '#ffedd5' },
  { v: 3, label: '普通',   en: 'Average',      color: '#eab308', bg: '#fef9c3' },
  { v: 4, label: '良好',   en: 'Good',         color: '#84cc16', bg: '#f7fee7' },
  { v: 5, label: '優秀',   en: 'Excellent',    color: '#22c55e', bg: '#dcfce7' },
]

const scoreColor = s => s >= 23 ? '#22c55e' : s >= 18 ? '#84cc16' : s >= 13 ? '#eab308' : s >= 8 ? '#f97316' : '#ef4444'
const scoreLabel = s => s >= 23 ? '優秀' : s >= 18 ? '良好' : s >= 13 ? '普通' : s >= 8 ? '待改善' : '不合格'
const dimColor = v => SCORE_OPTIONS[v - 1]?.color || C.muted

function getThisMonday() {
  const d = new Date()
  const diff = d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1)
  d.setDate(diff)
  return d.toISOString().slice(0, 10)
}

function monthKey(d) { return (d || '').slice(0, 7) }
function weekLabel(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return `${d.getMonth() + 1}/${d.getDate()}`
}

function exportCSV(filename, headers, rows) {
  const csv = [headers, ...rows]
    .map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(','))
    .join('\n')
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

// ── Charts ────────────────────────────────────────────────────────────────────

function RadarChart({ scores, size = 150 }) {
  const keys = FIVE_S.map(s => s.key)
  const n = keys.length
  const cx = size / 2, cy = size / 2
  const r = (size / 2) * 0.68
  const angle = i => (Math.PI * 2 * i / n) - Math.PI / 2
  const pt = (i, ratio) => ({
    x: cx + Math.cos(angle(i)) * r * ratio,
    y: cy + Math.sin(angle(i)) * r * ratio,
  })
  const toPath = pts => pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') + 'Z'

  const gridPaths = [1, 2, 3, 4, 5].map(level => toPath(keys.map((_, i) => pt(i, level / 5))))
  const scorePoints = keys.map((k, i) => pt(i, (scores[k] || 0) / 5))
  const hasData = Object.values(scores).some(v => v > 0)

  return (
    <svg width={size} height={size} style={{ overflow: 'visible', display: 'block' }}>
      {gridPaths.map((d, i) => (
        <path key={i} d={d} fill="none" stroke={i === 4 ? '#cbd5e1' : '#e2e8f0'} strokeWidth={i === 4 ? 1.5 : 0.8} />
      ))}
      {keys.map((_, i) => {
        const o = pt(i, 1)
        return <line key={i} x1={cx} y1={cy} x2={o.x} y2={o.y} stroke="#e2e8f0" strokeWidth={0.8} />
      })}
      {hasData && (
        <path d={toPath(scorePoints)} fill="#3b82f626" stroke="#3b82f6" strokeWidth={2} strokeLinejoin="round" />
      )}
      {hasData && scorePoints.map((p, i) => scores[keys[i]] > 0 && (
        <circle key={i} cx={p.x} cy={p.y} r={4} fill={FIVE_S[i].color} stroke="#fff" strokeWidth={1.5} />
      ))}
      {keys.map((k, i) => {
        const lp = pt(i, 1.26)
        return (
          <text key={k} x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={700} fill={FIVE_S[i].color}>
            {FIVE_S[i].jp}
          </text>
        )
      })}
    </svg>
  )
}

function DimBar({ score, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 7, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${(score / 5) * 100}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 800, color: color || C.muted, width: 14, textAlign: 'right' }}>{score || 0}</span>
    </div>
  )
}

function ColBarChart({ data, color = C.accent, height = 110 }) {
  const maxVal = Math.max(...data.map(d => d.value || 0), 1)
  if (!data.length) return null
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, height: '100%', justifyContent: 'flex-end' }}>
            {d.value > 0 && <div style={{ fontSize: 9, color, fontWeight: 700, lineHeight: 1 }}>{typeof d.value === 'number' && d.value % 1 !== 0 ? d.value.toFixed(1) : d.value}</div>}
            <div style={{ width: '100%', background: color, borderRadius: '3px 3px 0 0', height: `${(d.value || 0) / maxVal * 100}%`, minHeight: d.value > 0 ? 3 : 0, opacity: 0.82, transition: 'height 0.4s' }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</div>
        ))}
      </div>
    </div>
  )
}

// ── Helper card wrapper ───────────────────────────────────────────────────────
const SCard = ({ title, subtitle, action, children, accent }) => (
  <div style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${accent || C.border}`, padding: '16px 18px' }}>
    {(title || action) && (
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {action}
      </div>
    )}
    {children}
  </div>
)

// ── Score summary strip ───────────────────────────────────────────────────────
function ScoreSummary({ scores }) {
  const total = Object.values(scores).reduce((a, b) => a + b, 0)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <RadarChart scores={scores} size={150} />
      <div style={{ flex: 1, minWidth: 140 }}>
        {FIVE_S.map(s => (
          <div key={s.key} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{s.icon} {s.jp}</span>
              <span style={{ fontSize: 11, color: scores[s.key] > 0 ? dimColor(scores[s.key]) : C.muted, fontWeight: 700 }}>
                {scores[s.key] > 0 ? SCORE_OPTIONS[scores[s.key] - 1]?.label : '未評分'}
              </span>
            </div>
            <DimBar score={scores[s.key]} color={s.color} />
          </div>
        ))}
        <div style={{ marginTop: 10, textAlign: 'center', padding: '8px', background: total > 0 ? scoreColor(total) + '18' : '#f8fafc', borderRadius: 10, border: `1.5px solid ${total > 0 ? scoreColor(total) : C.border}` }}>
          <span style={{ fontSize: 26, fontWeight: 900, color: total > 0 ? scoreColor(total) : C.muted }}>{total}</span>
          <span style={{ fontSize: 12, color: C.muted }}>/25</span>
          {total > 0 && <div style={{ fontSize: 12, fontWeight: 700, color: scoreColor(total) }}>{scoreLabel(total)}</div>}
        </div>
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function FiveS({ userProfile, showToast, addLog, isMobile = false, enabledModules = [] }) {
  const isAdmin = userProfile?.role === 'admin'
  const pos = userProfile?.position || 'user'
  const canManage = isAdmin || enabledModules.includes('assign') || ['factory_manager', 'senior_manager', 'section_chief'].includes(pos)

  const { lang } = useAppPrefs()
  const _ = makeT(lang)

  const SCORE_OPTIONS = [
    { v: 1, label: _('fivesScoreUnacceptable'), color: '#ef4444', bg: '#fee2e2' },
    { v: 2, label: _('fivesScorePoor'),         color: '#f97316', bg: '#ffedd5' },
    { v: 3, label: _('fivesScoreAverage'),      color: '#eab308', bg: '#fef9c3' },
    { v: 4, label: _('fivesScoreGood'),         color: '#84cc16', bg: '#f7fee7' },
    { v: 5, label: _('fivesScoreExcellent'),    color: '#22c55e', bg: '#dcfce7' },
  ]
  const scoreLabel = s => s >= 23 ? _('fivesScoreExcellent') : s >= 18 ? _('fivesScoreGood') : s >= 13 ? _('fivesScoreAverage') : s >= 8 ? _('fivesScorePoor') : _('fivesScoreUnacceptable')

  const [activeTab, setActiveTab] = useState('my')
  const [templates, setTemplates] = useState([])
  const [submissions, setSubmissions] = useState([])
  const [users, setUsers] = useState([])
  const [saving, setSaving] = useState(false)
  const [currentTemplate, setCurrentTemplate] = useState(null)
  const [mySubmission, setMySubmission] = useState(null)
  const [scores, setScores] = useState({ seiri: 0, seiton: 0, seiso: 0, seiketsu: 0, shitsuke: 0 })
  const [notes, setNotes] = useState({ seiri: '', seiton: '', seiso: '', seiketsu: '', shitsuke: '' })

  // Admin: create template
  const [showAddTemplate, setShowAddTemplate] = useState(false)
  const [templateForm, setTemplateForm] = useState({ title: '', week_start: getThisMonday(), assigned_users: [] })
  const [deptFilter, setDeptFilter] = useState('all')
  const [userSearch, setUserSearch] = useState('')
  const [expandedTemplate, setExpandedTemplate] = useState(null)
  const [detailSub, setDetailSub] = useState(null)  // submission detail modal

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = async () => {
    const cid = userProfile?.company_id
    let tmpQ = supabase.from('fives_templates').select('*').order('week_start', { ascending: false }).limit(100)
    if (cid) tmpQ = tmpQ.eq('company_id', cid)
    let subQ = supabase.from('fives_submissions').select('*').order('submitted_at', { ascending: false }).limit(1000)
    if (cid) subQ = subQ.eq('company_id', cid)
    let usrQ = supabase.from('user_profiles').select('id, name, position, department').eq('approved', true).order('name').limit(200)
    if (cid) usrQ = usrQ.eq('company_id', cid)
    const [{ data: tmps }, { data: subs }, { data: usrs }] = await Promise.all([tmpQ, subQ, usrQ])
    if (tmps) setTemplates(tmps)
    if (subs) setSubmissions(subs)
    if (usrs) setUsers(usrs)

    if (tmps && userProfile?.id) {
      const active = tmps.find(t => t.is_active && t.assigned_users?.includes(userProfile.id))
      setCurrentTemplate(active || null)
      if (active && subs) {
        const existing = subs.find(s => s.template_id === active.id && s.user_id === userProfile.id)
        if (existing) {
          setMySubmission(existing)
          setScores({ seiri: existing.seiri, seiton: existing.seiton, seiso: existing.seiso, seiketsu: existing.seiketsu, shitsuke: existing.shitsuke })
          setNotes({ seiri: existing.seiri_note || '', seiton: existing.seiton_note || '', seiso: existing.seiso_note || '', seiketsu: existing.seiketsu_note || '', shitsuke: existing.shitsuke_note || '' })
        } else {
          setMySubmission(null)
          setScores({ seiri: 0, seiton: 0, seiso: 0, seiketsu: 0, shitsuke: 0 })
          setNotes({ seiri: '', seiton: '', seiso: '', seiketsu: '', shitsuke: '' })
        }
      }
    }
  }

  useEffect(() => { load() }, [userProfile?.id])

  const totalScore = Object.values(scores).reduce((a, b) => a + b, 0)
  const allFilled = Object.values(scores).every(s => s > 0)
  const lowScores = FIVE_S.filter(s => scores[s.key] > 0 && scores[s.key] <= 2)

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!allFilled) { showToast('請為所有 5S 項目評分', 'error'); return }
    setSaving(true)
    const payload = {
      template_id: currentTemplate.id, user_id: userProfile.id, user_name: userProfile.name,
      week_start: currentTemplate.week_start, ...scores,
      seiri_note: notes.seiri, seiton_note: notes.seiton, seiso_note: notes.seiso,
      seiketsu_note: notes.seiketsu, shitsuke_note: notes.shitsuke,
      total_score: totalScore, updated_at: new Date().toISOString(),
    }
    if (mySubmission) {
      await supabase.from('fives_submissions').update(payload).eq('id', mySubmission.id)
    } else {
      const { data } = await supabase.from('fives_submissions').insert({ ...payload, submitted_at: new Date().toISOString(), company_id: userProfile?.company_id || null }).select().single()
      if (data) setMySubmission(data)
    }
    await addLog('提交5S', `本週5S評分：${totalScore}/25 · ${scoreLabel(totalScore)}`)
    showToast(`✅ 5S 評分已${mySubmission ? '更新' : '提交'}！${scoreLabel(totalScore)} ${totalScore}/25`)
    setSaving(false)
    load()
  }

  // ── Save template ─────────────────────────────────────────────────────────
  const handleSaveTemplate = async () => {
    if (!templateForm.title) { showToast('請填寫表單名稱', 'error'); return }
    if (templateForm.assigned_users.length === 0) { showToast('請選擇填寫人員', 'error'); return }
    setSaving(true)
    await supabase.from('fives_templates').insert({ ...templateForm, created_by: userProfile?.name, is_active: true, company_id: userProfile?.company_id || null })
    await addLog('建立5S表單', `${templateForm.title}，指派 ${templateForm.assigned_users.length} 人`)
    showToast(`✅ 5S 表單「${templateForm.title}」已建立`)
    setShowAddTemplate(false)
    setTemplateForm({ title: '', week_start: getThisMonday(), assigned_users: [] })
    setSaving(false)
    load()
  }

  // ── Copy last template ────────────────────────────────────────────────────
  const handleCopyLast = () => {
    if (!templates.length) return
    const last = templates[0]
    setTemplateForm({
      title: `${getThisMonday().slice(0, 7).replace('-', 'W').slice(2)} ${last.title.replace(/^\d{2}W\d{2}\s*/, '').trim()}`,
      week_start: getThisMonday(),
      assigned_users: last.assigned_users || [],
    })
    setShowAddTemplate(true)
  }

  // ── User filter helpers ───────────────────────────────────────────────────
  const departments = useMemo(() => {
    const depts = [...new Set(users.map(u => u.department).filter(Boolean))]
    return ['all', ...depts]
  }, [users])

  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const matchDept = deptFilter === 'all' || u.department === deptFilter
      const matchSearch = !userSearch || u.name.includes(userSearch)
      return matchDept && matchSearch
    })
  }, [users, deptFilter, userSearch])

  // ── My history ────────────────────────────────────────────────────────────
  const myHistory = useMemo(() => {
    if (!userProfile?.id) return []
    return submissions
      .filter(s => s.user_id === userProfile.id)
      .sort((a, b) => (b.week_start || b.submitted_at || '').localeCompare(a.week_start || a.submitted_at || ''))
  }, [submissions, userProfile?.id])

  // ── Analytics ─────────────────────────────────────────────────────────────
  const analytics = useMemo(() => {
    if (!submissions.length) return null

    // Weekly avg score (last 8 weeks, by template week_start)
    const templateMap = Object.fromEntries(templates.map(t => [t.id, t]))
    const weekMap = {}
    submissions.forEach(s => {
      const wk = s.week_start || (s.submitted_at || '').slice(0, 10)
      if (!weekMap[wk]) weekMap[wk] = []
      weekMap[wk].push(s.total_score)
    })
    const weeks8 = Object.keys(weekMap).sort().slice(-8)
    const weeklyAvg = weeks8.map(wk => ({
      label: weekLabel(wk),
      value: Math.round(weekMap[wk].reduce((a, v) => a + v, 0) / weekMap[wk].length * 10) / 10,
    }))

    // Dimension averages
    const dimAvg = FIVE_S.map(s => ({
      key: s.key, jp: s.jp, color: s.color,
      value: Math.round(submissions.reduce((a, sub) => a + (sub[s.key] || 0), 0) / submissions.length * 10) / 10,
    }))

    // User ranking (avg total score, min 2 submissions)
    const userMap = {}
    submissions.forEach(s => {
      if (!userMap[s.user_name]) userMap[s.user_name] = []
      userMap[s.user_name].push(s.total_score)
    })
    const userRanking = Object.entries(userMap)
      .map(([name, arr]) => ({
        name,
        avg: Math.round(arr.reduce((a, v) => a + v, 0) / arr.length * 10) / 10,
        count: arr.length,
      }))
      .filter(u => u.count >= 1)
      .sort((a, b) => b.avg - a.avg)

    // Monthly submission count (last 6 months)
    const now = new Date()
    const months6 = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now); d.setDate(1); d.setMonth(d.getMonth() - (5 - i))
      return d.toISOString().slice(0, 7)
    })
    const monthlyCount = months6.map(m => ({
      label: `${parseInt(m.slice(5))}月`,
      value: submissions.filter(s => (s.week_start || s.submitted_at || '').slice(0, 7) === m).length,
    }))

    // This week stats
    const thisWeekTmpl = templates.find(t => t.is_active)
    const thisWeekSubs = thisWeekTmpl ? submissions.filter(s => s.template_id === thisWeekTmpl.id) : []
    const thisWeekAvg = thisWeekSubs.length > 0
      ? Math.round(thisWeekSubs.reduce((a, s) => a + s.total_score, 0) / thisWeekSubs.length * 10) / 10
      : 0
    const assignedCount = thisWeekTmpl?.assigned_users?.length || 0
    const submitRate = assignedCount > 0 ? Math.round(thisWeekSubs.length / assignedCount * 100) : 0

    // Lowest scoring dimension overall
    const lowestDim = [...dimAvg].sort((a, b) => a.value - b.value)[0]

    return { weeklyAvg, dimAvg, userRanking, monthlyCount, thisWeekAvg, submitRate, thisWeekSubs, assignedCount, lowestDim }
  }, [submissions, templates])

  // ── Tabs config ───────────────────────────────────────────────────────────
  const TABS = [
    { key: 'my',      icon: '📝', label: _('fivesTabMyScore') },
    { key: 'history', icon: '📜', label: _('fivesTabHistory') },
    ...(canManage ? [
      { key: 'admin',     icon: '⚙️',  label: _('fivesTabManage') },
      { key: 'analytics', icon: '📊',  label: _('fivesTabAnalytics') },
    ] : []),
  ]

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader title="5S 管理" en="5S Management" vi="5S" />

      {/* Score legend */}
      <div style={{ display: 'flex', gap: 5, marginBottom: 20, flexWrap: 'wrap', padding: '10px 14px', background: '#f8fafc', borderRadius: 12, border: `1.5px solid ${C.border}` }}>
        <span style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginRight: 4, alignSelf: 'center' }}>評分：</span>
        {SCORE_OPTIONS.map(s => (
          <span key={s.v} style={{ fontSize: 12, fontWeight: 700, color: s.color, padding: '3px 9px', background: s.bg, borderRadius: 20, border: `1.5px solid ${s.color}44` }}>
            {s.v} {s.label}
          </span>
        ))}
        <span style={{ fontSize: 11, color: C.muted, alignSelf: 'center', marginLeft: 'auto' }}>滿分 25 分</span>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} style={{ ...st.btn(activeTab === t.key ? 'primary' : 'default'), padding: '10px 18px', fontSize: 13 }} onClick={() => setActiveTab(t.key)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════ TAB: 我的評分 ══════════════ */}
      {activeTab === 'my' && (
        <>
          {!currentTemplate ? (
            <div style={{ ...st.card, textAlign: 'center', padding: '48px 24px' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{_('fivesNoFormThisWeek')}</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>{_('fivesContactAdmin')}</div>
              {myHistory.length > 0 && (
                <button style={{ ...st.btn('default'), padding: '10px 20px', marginTop: 16, fontSize: 13 }} onClick={() => setActiveTab('history')}>
                  📜 {_('fivesViewHistory')}
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Form header with summary */}
              <div style={{ ...st.card, marginBottom: 16, background: C.accentDim, border: `2px solid ${C.accent}`, borderRadius: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: C.accent }}>{currentTemplate.title}</div>
                    <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>📅 週起始：{formatDate(currentTemplate.week_start)}</div>
                    {mySubmission && (
                      <div style={{ fontSize: 12, color: '#22c55e', fontWeight: 700, marginTop: 4 }}>
                        ✅ {_('fivesAlreadySubmitted')} · {formatDate(mySubmission.updated_at)}
                      </div>
                    )}
                  </div>
                </div>
                <ScoreSummary scores={scores} />
                {/* Low score alert */}
                {lowScores.length > 0 && (
                  <div style={{ marginTop: 12, background: '#fff7ed', border: '1.5px solid #fb923c', borderRadius: 10, padding: '10px 14px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#f97316', marginBottom: 4 }}>⚠️ 低分項目需注意</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {lowScores.map(s => (
                        <span key={s.key} style={{ fontSize: 12, background: s.bg, color: s.color, padding: '3px 10px', borderRadius: 20, fontWeight: 700, border: `1px solid ${s.color}44` }}>
                          {s.icon} {s.jp} — {SCORE_OPTIONS[scores[s.key] - 1]?.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 5S Cards */}
              {FIVE_S.map(s => (
                <div key={s.key} style={{ ...st.card, marginBottom: 12, borderLeft: `5px solid ${scores[s.key] > 0 ? dimColor(scores[s.key]) : C.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 28, flexShrink: 0 }}>{s.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.jp}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{s.zh}</span>
                        <span style={{ fontSize: 11, color: C.muted }}>· {s.en}</span>
                      </div>
                      <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>{s.desc}</div>
                    </div>
                    {scores[s.key] > 0 && (
                      <div style={{ background: SCORE_OPTIONS[scores[s.key]-1]?.bg, border: `2px solid ${dimColor(scores[s.key])}`, borderRadius: 12, padding: '6px 14px', textAlign: 'center', flexShrink: 0 }}>
                        <div style={{ fontSize: 22, fontWeight: 900, color: dimColor(scores[s.key]), lineHeight: 1 }}>{scores[s.key]}</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: dimColor(scores[s.key]) }}>{SCORE_OPTIONS[scores[s.key]-1]?.label}</div>
                      </div>
                    )}
                  </div>

                  {/* Checklist */}
                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: '8px 12px', marginBottom: 12 }}>
                    {s.items.map((item, i) => (
                      <div key={i} style={{ fontSize: 12, color: C.sub, padding: '5px 0', borderBottom: i < s.items.length - 1 ? `1px solid ${C.border}` : 'none', lineHeight: 1.5, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: s.color, flexShrink: 0, fontSize: 10 }}>▸</span>
                        {item}
                      </div>
                    ))}
                  </div>

                  {/* Score buttons */}
                  <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
                    {SCORE_OPTIONS.map(sl => (
                      <button key={sl.v} onClick={() => setScores(sc => ({ ...sc, [s.key]: sl.v }))}
                        style={{ flex: 1, padding: '11px 4px', borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: 'pointer', minHeight: 54, border: `2px solid ${scores[s.key] === sl.v ? sl.color : C.border}`, background: scores[s.key] === sl.v ? sl.color + '20' : '#fff', color: scores[s.key] === sl.v ? sl.color : C.muted, transition: 'all 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <span style={{ fontSize: 15 }}>{sl.v}</span>
                        <span style={{ fontSize: 9, opacity: 0.8 }}>{sl.label}</span>
                      </button>
                    ))}
                  </div>

                  <input style={{ ...st.input, fontSize: 12 }} value={notes[s.key]}
                    placeholder={`備注（選填）— 例：${s.key === 'seiri' ? '已清除A區過期物料' : s.key === 'seiton' ? '工具架標示已更新' : '待改善項目說明'}`}
                    onChange={e => setNotes(n => ({ ...n, [s.key]: e.target.value }))} />
                </div>
              ))}

              {/* Submit */}
              <button style={{ ...st.btn(allFilled ? 'success' : 'default'), width: '100%', padding: '16px', fontSize: 15, minHeight: 58, marginBottom: 24 }}
                onClick={handleSubmit} disabled={saving || !allFilled}>
                <span>{saving ? _('loading') : mySubmission ? _('fivesUpdateScore') : _('fivesSubmitWeekly')}</span>
                <span style={{ fontSize: 11, opacity: 0.7, display: 'block', marginTop: 2 }}>
                  {allFilled ? `總分 ${totalScore}/25 · ${scoreLabel(totalScore)}` : `還有 ${FIVE_S.filter(s => !scores[s.key]).length} 個項目未評分`}
                </span>
              </button>
            </>
          )}
        </>
      )}

      {/* ══════════════ TAB: 歷史紀錄 ══════════════ */}
      {activeTab === 'history' && (
        <>
          {myHistory.length === 0 ? (
            <div style={st.card}><EmptyState icon="📜" text={_('fivesNoHistory')} /></div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>
                共 <strong style={{ color: C.text }}>{myHistory.length}</strong> 筆紀錄 ·
                平均 <strong style={{ color: scoreColor(Math.round(myHistory.reduce((a, s) => a + s.total_score, 0) / myHistory.length)) }}>
                  {(myHistory.reduce((a, s) => a + s.total_score, 0) / myHistory.length).toFixed(1)}
                </strong>/25
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {myHistory.map(sub => {
                  const tmpl = templates.find(t => t.id === sub.template_id)
                  const sc = { seiri: sub.seiri, seiton: sub.seiton, seiso: sub.seiso, seiketsu: sub.seiketsu, shitsuke: sub.shitsuke }
                  return (
                    <div key={sub.id} style={{ ...st.card, borderLeft: `5px solid ${scoreColor(sub.total_score)}` }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{tmpl?.title || '—'}</div>
                          <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
                            📅 {formatDate(sub.week_start || sub.submitted_at)}
                            {sub.submitted_at && <span style={{ marginLeft: 8 }}>提交：{formatDate(sub.submitted_at)}</span>}
                          </div>
                        </div>
                        <div style={{ background: scoreColor(sub.total_score) + '18', border: `2px solid ${scoreColor(sub.total_score)}`, borderRadius: 12, padding: '6px 16px', textAlign: 'center', flexShrink: 0 }}>
                          <div style={{ fontSize: 24, fontWeight: 900, color: scoreColor(sub.total_score), lineHeight: 1 }}>{sub.total_score}</div>
                          <div style={{ fontSize: 10, color: scoreColor(sub.total_score), fontWeight: 700 }}>{scoreLabel(sub.total_score)}</div>
                        </div>
                      </div>

                      {/* Dimension summary */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                        {FIVE_S.map(s => (
                          <div key={s.key} style={{ background: sub[s.key] > 0 ? SCORE_OPTIONS[sub[s.key]-1]?.bg : '#f8fafc', border: `1.5px solid ${sub[s.key] > 0 ? dimColor(sub[s.key]) : C.border}`, borderRadius: 10, padding: '5px 10px', textAlign: 'center', flex: '1 1 55px' }}>
                            <div style={{ fontSize: 9, color: C.muted }}>{s.icon} {s.jp}</div>
                            <div style={{ fontSize: 16, fontWeight: 900, color: sub[s.key] > 0 ? dimColor(sub[s.key]) : C.muted }}>{sub[s.key] || '-'}</div>
                          </div>
                        ))}
                      </div>

                      {/* Radar mini */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <RadarChart scores={sc} size={110} />
                        <div style={{ flex: 1, minWidth: 140 }}>
                          {FIVE_S.map(s => (
                            <div key={s.key} style={{ marginBottom: 5 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                <span style={{ fontSize: 11, color: C.text }}>{s.jp}</span>
                                <span style={{ fontSize: 10, color: sub[s.key] > 0 ? dimColor(sub[s.key]) : C.muted, fontWeight: 700 }}>{SCORE_OPTIONS[sub[s.key]-1]?.label || '-'}</span>
                              </div>
                              <DimBar score={sub[s.key]} color={s.color} />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Notes if any */}
                      {FIVE_S.some(s => sub[s.key + '_note']) && (
                        <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 10, padding: '8px 12px' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 6 }}>📝 備注</div>
                          {FIVE_S.filter(s => sub[s.key + '_note']).map(s => (
                            <div key={s.key} style={{ fontSize: 12, color: C.text, marginBottom: 4 }}>
                              <span style={{ color: s.color, fontWeight: 700 }}>{s.jp}：</span>
                              {sub[s.key + '_note']}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </>
      )}

      {/* ══════════════ TAB: 管理表單 ══════════════ */}
      {activeTab === 'admin' && canManage && (
        <>
          {/* Action bar */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button style={{ ...st.btn(), padding: '10px 18px', fontSize: 13 }} onClick={() => { setShowAddTemplate(!showAddTemplate); setDeptFilter('all'); setUserSearch('') }}>
              {showAddTemplate ? `✕ ${_('cancel')}` : `+ ${_('fivesCreateForm')}`}
            </button>
            {templates.length > 0 && !showAddTemplate && (
              <button style={{ ...st.btn('default'), padding: '10px 18px', fontSize: 13 }} onClick={handleCopyLast}>
                📋 {_('fivesCopyLast')}
              </button>
            )}
            {submissions.length > 0 && (
              <button style={{ ...st.btn('default'), padding: '10px 18px', fontSize: 13, marginLeft: 'auto' }}
                onClick={() => exportCSV(
                  `5S評分記錄_${new Date().toLocaleDateString('zh-TW').replace(/\//g,'-')}.csv`,
                  ['員工', '週起始', '整理', '整頓', '清掃', '清潔', '素養', '總分', '評級', '提交時間'],
                  submissions.map(s => [s.user_name, s.week_start, s.seiri, s.seiton, s.seiso, s.seiketsu, s.shitsuke, s.total_score, scoreLabel(s.total_score), formatDate(s.submitted_at)])
                )}>
                📥 {_('fivesExportAll')}
              </button>
            )}
          </div>

          {/* Create form */}
          {showAddTemplate && (
            <div style={{ ...st.card, marginBottom: 16, border: `2px solid ${C.accent}` }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 16 }}>📋 建立 5S 表單</div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 16 }}>
                <div>
                  <label style={st.label}>{_('fivesFormTitle')}</label>
                  <input style={st.input} value={templateForm.title} placeholder="例：2025W16 生產線 5S 評核" onChange={e => setTemplateForm(f => ({ ...f, title: e.target.value }))} />
                </div>
                <div>
                  <label style={st.label}>{_('fivesWeekStart')}</label>
                  <input type="date" style={st.input} value={templateForm.week_start} onChange={e => setTemplateForm(f => ({ ...f, week_start: e.target.value }))} />
                </div>
              </div>

              {/* User selection */}
              <label style={st.label}>
                {_('fivesAssignUsers')}
                <span style={{ color: C.muted, fontWeight: 400, marginLeft: 6 }}>（已選 {templateForm.assigned_users.length} 人）</span>
              </label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                {/* Dept filter */}
                {departments.map(d => (
                  <button key={d} style={{ ...st.btn(deptFilter === d ? 'primary' : 'default'), padding: '6px 12px', fontSize: 12 }} onClick={() => setDeptFilter(d)}>
                    {d === 'all' ? '全部部門' : d}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <input style={{ ...st.input, flex: 1, fontSize: 13 }} placeholder="🔍 搜尋員工姓名" value={userSearch} onChange={e => setUserSearch(e.target.value)} />
                <button style={{ ...st.btn('default'), padding: '8px 14px', fontSize: 12 }}
                  onClick={() => {
                    const ids = filteredUsers.map(u => u.id)
                    const allSel = ids.every(id => templateForm.assigned_users.includes(id))
                    setTemplateForm(f => ({
                      ...f,
                      assigned_users: allSel
                        ? f.assigned_users.filter(id => !ids.includes(id))
                        : [...new Set([...f.assigned_users, ...ids])]
                    }))
                  }}>
                  {filteredUsers.every(u => templateForm.assigned_users.includes(u.id)) ? '✕ 取消全選' : '☑ 全選'}
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(165px, 1fr))', gap: 8, marginBottom: 16, maxHeight: 260, overflowY: 'auto', padding: 2 }}>
                {filteredUsers.map(u => {
                  const sel = templateForm.assigned_users.includes(u.id)
                  return (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, cursor: 'pointer', background: sel ? C.accentDim : '#f8fafc', border: `2px solid ${sel ? C.accent : C.border}`, transition: 'all 0.15s' }}>
                      <input type="checkbox" checked={sel} onChange={() => setTemplateForm(f => ({ ...f, assigned_users: sel ? f.assigned_users.filter(id => id !== u.id) : [...f.assigned_users, u.id] }))} style={{ width: 16, height: 16, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: sel ? 700 : 400, color: sel ? C.accent : C.text }}>{u.name}</div>
                        <div style={{ fontSize: 10, color: C.muted }}>{u.department || u.position}</div>
                      </div>
                    </label>
                  )
                })}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button style={{ ...st.btn(), flex: 1, padding: '13px' }} onClick={handleSaveTemplate} disabled={saving}>
                  {saving ? '建立中...' : `確認建立（指派 ${templateForm.assigned_users.length} 人）`}
                </button>
                <button style={{ ...st.btn('default'), padding: '13px 20px' }} onClick={() => setShowAddTemplate(false)}>{_('cancel')}</button>
              </div>
            </div>
          )}

          {/* Template list */}
          {templates.length === 0 ? (
            <div style={st.card}><EmptyState icon="📋" text={_('fivesNoForms')} /></div>
          ) : templates.map(t => {
            const tSubs = submissions.filter(s => s.template_id === t.id)
            const assignedCount = t.assigned_users?.length || 0
            const avgScore = tSubs.length > 0 ? Math.round(tSubs.reduce((a, s) => a + s.total_score, 0) / tSubs.length * 10) / 10 : 0
            const isExpanded = expandedTemplate === t.id
            const submitPct = assignedCount > 0 ? Math.round(tSubs.length / assignedCount * 100) : 0

            return (
              <div key={t.id} style={{ ...st.card, marginBottom: 12, border: `2px solid ${t.is_active ? C.accent : C.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{t.title}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: t.is_active ? '#dbeafe' : '#f1f5f9', color: t.is_active ? C.accent : C.muted, border: `1px solid ${t.is_active ? C.accent + '44' : C.border}` }}>
                        {t.is_active ? `✅ ${_('fivesFormActive')}` : `⏸ ${_('fivesFormInactive')}`}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                      📅 {formatDate(t.week_start)} · 建立：{t.created_by}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button style={{ ...st.btn(t.is_active ? 'warning' : 'success'), padding: '6px 12px', fontSize: 12 }}
                      onClick={async () => { await supabase.from('fives_templates').update({ is_active: !t.is_active }).eq('id', t.id); load() }}>
                      {t.is_active ? _('fivesDeactivate') : _('fivesActivate')}
                    </button>
                    <button style={{ ...st.btn('danger'), padding: '6px 12px', fontSize: 12 }}
                      onClick={async () => { if (!window.confirm(`確定刪除「${t.title}」？`)) return; await supabase.from('fives_templates').delete().eq('id', t.id); load() }}>
                      {_('fivesDelete')}
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                  {[
                    { label: '指派人數', value: assignedCount, color: C.accent },
                    { label: '已提交', value: `${tSubs.length} 人`, color: tSubs.length === assignedCount && assignedCount > 0 ? '#22c55e' : '#eab308' },
                    { label: '平均分', value: avgScore > 0 ? `${avgScore}/25` : '—', color: avgScore > 0 ? scoreColor(Math.round(avgScore)) : C.muted },
                    { label: '評級', value: avgScore > 0 ? scoreLabel(Math.round(avgScore)) : '—', color: avgScore > 0 ? scoreColor(Math.round(avgScore)) : C.muted },
                  ].map(s => (
                    <div key={s.label} style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 14px', border: `1.5px solid ${C.border}`, textAlign: 'center', flex: '1 1 70px' }}>
                      <div style={{ fontSize: 18, fontWeight: 900, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Submission progress bar */}
                {assignedCount > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.muted, marginBottom: 4 }}>
                      <span>提交進度</span>
                      <span style={{ fontWeight: 700, color: submitPct === 100 ? '#22c55e' : '#f59e0b' }}>{submitPct}%（{tSubs.length}/{assignedCount}）</span>
                    </div>
                    <div style={{ background: '#f1f5f9', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                      <div style={{ width: `${submitPct}%`, height: '100%', background: submitPct === 100 ? '#22c55e' : '#f59e0b', borderRadius: 6, transition: 'width 0.4s' }} />
                    </div>
                    {/* Missing users */}
                    {submitPct < 100 && t.is_active && (
                      <div style={{ marginTop: 6, fontSize: 11, color: C.muted }}>
                        ⏳ 未提交：{(t.assigned_users || []).filter(uid => !tSubs.find(s => s.user_id === uid)).map(uid => users.find(u => u.id === uid)?.name || uid).join('、')}
                      </div>
                    )}
                  </div>
                )}

                {/* Expand details */}
                {tSubs.length > 0 && (
                  <>
                    <button style={{ ...st.btn('default'), padding: '8px 16px', fontSize: 12, width: '100%' }}
                      onClick={() => setExpandedTemplate(isExpanded ? null : t.id)}>
                      {isExpanded ? '▲ 收起明細' : `▼ 查看 ${tSubs.length} 筆填寫明細`}
                    </button>
                    {isExpanded && (
                      <div style={{ marginTop: 12 }}>
                        {/* Per-dimension averages */}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                          {FIVE_S.map(s => {
                            const avg = tSubs.reduce((a, sub) => a + (sub[s.key] || 0), 0) / tSubs.length
                            return (
                              <div key={s.key} style={{ flex: '1 1 70px', background: s.bg, border: `1.5px solid ${s.color}44`, borderRadius: 10, padding: '8px 10px', textAlign: 'center' }}>
                                <div style={{ fontSize: 11, color: s.color }}>{s.icon} {s.jp}</div>
                                <div style={{ fontSize: 18, fontWeight: 900, color: s.color }}>{avg.toFixed(1)}</div>
                              </div>
                            )
                          })}
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                            <thead>
                              <tr>
                                {['員工', _('fivesDimSeiri'), _('fivesDimSeiton'), _('fivesDimSeiso'), _('fivesDimSeiketsu'), _('fivesDimShitsuke'), '總分', '提交時間', ''].map((h, i) => (
                                  <th key={i} style={st.th}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {tSubs.sort((a, b) => b.total_score - a.total_score).map(sub => (
                                <tr key={sub.id}>
                                  <td style={{ ...st.td, fontWeight: 700, color: C.text }}>{sub.user_name}</td>
                                  {['seiri','seiton','seiso','seiketsu','shitsuke'].map(k => (
                                    <td key={k} style={{ ...st.td, textAlign: 'center' }}>
                                      <span style={{ fontWeight: 800, color: dimColor(sub[k]), fontSize: 15 }}>{sub[k]}</span>
                                    </td>
                                  ))}
                                  <td style={{ ...st.td, textAlign: 'center' }}>
                                    <span style={{ fontSize: 17, fontWeight: 900, color: scoreColor(sub.total_score) }}>{sub.total_score}</span>
                                    <div style={{ fontSize: 9, color: scoreColor(sub.total_score), fontWeight: 700 }}>{scoreLabel(sub.total_score)}</div>
                                  </td>
                                  <td style={{ ...st.td, fontSize: 11, color: C.muted }}>{formatDate(sub.submitted_at)}</td>
                                  <td style={{ ...st.td }}>
                                    <button style={{ ...st.btn('default'), padding: '4px 10px', fontSize: 11 }} onClick={() => setDetailSub(sub)}>
                                      詳情
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </>
      )}

      {/* ══════════════ TAB: 統計分析 ══════════════ */}
      {activeTab === 'analytics' && canManage && (
        <>
          {!analytics ? (
            <div style={st.card}><EmptyState icon="📊" text={_('fivesNoDataAnalytics')} /></div>
          ) : (
            <>
              {/* KPI cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
                {[
                  { icon: '📊', label: _('fivesThisWeekAvg'), value: analytics.thisWeekAvg > 0 ? `${analytics.thisWeekAvg}/25` : '—', color: analytics.thisWeekAvg > 0 ? scoreColor(Math.round(analytics.thisWeekAvg)) : C.muted, bg: analytics.thisWeekAvg > 0 ? scoreColor(Math.round(analytics.thisWeekAvg)) + '18' : '#f8fafc', sub: analytics.thisWeekAvg > 0 ? scoreLabel(Math.round(analytics.thisWeekAvg)) : '本週尚無資料' },
                  { icon: '✅', label: _('fivesThisWeekRate'), value: `${analytics.submitRate}%`, color: analytics.submitRate === 100 ? '#22c55e' : analytics.submitRate >= 50 ? '#eab308' : '#ef4444', bg: analytics.submitRate === 100 ? '#dcfce7' : analytics.submitRate >= 50 ? '#fef9c3' : '#fee2e2', sub: `${analytics.thisWeekSubs.length}/${analytics.assignedCount} 人已提交` },
                  { icon: '🏆', label: _('fivesTopEmployee'), value: analytics.userRanking[0]?.name || '—', color: '#f59e0b', bg: '#fffbeb', sub: analytics.userRanking[0] ? `平均 ${analytics.userRanking[0].avg}/25` : '' },
                  { icon: '⚠️', label: '最需改善項', value: analytics.lowestDim ? analytics.lowestDim.jp : '—', color: analytics.lowestDim?.color || C.muted, bg: analytics.lowestDim ? analytics.lowestDim.color + '18' : '#f8fafc', sub: analytics.lowestDim ? `平均 ${analytics.lowestDim.value}/5` : '' },
                  { icon: '📝', label: '累計評分筆數', value: submissions.length, color: C.accent, bg: C.accentDim, sub: `${users.filter(u => submissions.find(s => s.user_id === u.id)).length} 位員工` },
                ].map(c => (
                  <div key={c.label} style={{ background: c.bg, border: `1.5px solid ${c.color}33`, borderRadius: 14, padding: '14px 15px' }}>
                    <div style={{ fontSize: 20 }}>{c.icon}</div>
                    <div style={{ fontSize: typeof c.value === 'string' && c.value.length > 6 ? 16 : 22, fontWeight: 900, color: c.color, marginTop: 4, lineHeight: 1.1, wordBreak: 'break-all' }}>{c.value}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: c.color, marginTop: 4 }}>{c.label}</div>
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{c.sub}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>

                {/* Weekly avg trend */}
                <SCard title={`📈 ${_('fivesWeeklyTrend')}`} subtitle="近 8 次表單平均得分">
                  <ColBarChart data={analytics.weeklyAvg} color={C.accent} height={120} />
                </SCard>

                {/* Monthly submission count */}
                <SCard title={`📅 ${_('fivesMonthlySubmit')}`} subtitle="近 6 個月評分提交統計">
                  <ColBarChart data={analytics.monthlyCount} color="#7c3aed" height={120} />
                </SCard>

                {/* Dimension average */}
                <SCard title={`🎯 ${_('fivesDimAvg')}`} subtitle="全部歷史評分的 5S 各維度均值">
                  {FIVE_S.map((s, i) => {
                    const dim = analytics.dimAvg.find(d => d.key === s.key)
                    const val = dim?.value || 0
                    return (
                      <div key={s.key} style={{ marginBottom: i < FIVE_S.length - 1 ? 12 : 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{s.icon} {s.jp} <span style={{ fontSize: 11, color: C.muted }}>{s.zh}</span></span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 14, fontWeight: 900, color: s.color }}>{val.toFixed(1)}</span>
                            <span style={{ fontSize: 10, color: C.muted }}>/5</span>
                            <span style={{ fontSize: 10, fontWeight: 700, color: s.color, background: s.bg, padding: '1px 6px', borderRadius: 8 }}>
                              {SCORE_OPTIONS[Math.round(val)-1]?.label || '—'}
                            </span>
                          </div>
                        </div>
                        <div style={{ background: '#f1f5f9', borderRadius: 6, height: 8 }}>
                          <div style={{ width: `${(val / 5) * 100}%`, height: '100%', background: s.color, borderRadius: 6, transition: 'width 0.4s' }} />
                        </div>
                      </div>
                    )
                  })}
                </SCard>

                {/* User ranking */}
                <SCard
                  title={`🏅 ${_('fivesEmployeeRanking')}`}
                  subtitle="依歷史平均總分排序"
                  action={
                    <button style={{ ...st.btn('default'), padding: '5px 10px', fontSize: 11 }}
                      onClick={() => exportCSV(
                        '5S員工排行.csv',
                        ['排名', '員工', '平均分', '提交次數', '評級'],
                        analytics.userRanking.map((u, i) => [i+1, u.name, u.avg, u.count, scoreLabel(Math.round(u.avg))])
                      )}>
                      📥 匯出
                    </button>
                  }
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {analytics.userRanking.slice(0, 10).map((u, i) => {
                      const c = scoreColor(Math.round(u.avg))
                      const maxAvg = analytics.userRanking[0]?.avg || 1
                      return (
                        <div key={u.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ fontSize: 12, color: i < 3 ? c : C.muted, fontWeight: i < 3 ? 900 : 400, width: 20, textAlign: 'right', flexShrink: 0 }}>#{i + 1}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{u.name}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 13, fontWeight: 900, color: c }}>{u.avg}</span>
                                <span style={{ fontSize: 10, color: C.muted }}>({u.count}次)</span>
                                <span style={{ fontSize: 10, fontWeight: 700, color: c, background: c + '18', padding: '1px 6px', borderRadius: 8 }}>{scoreLabel(Math.round(u.avg))}</span>
                              </div>
                            </div>
                            <div style={{ background: '#f1f5f9', borderRadius: 4, height: 6 }}>
                              <div style={{ width: `${(u.avg / 25) * 100}%`, height: '100%', background: c, borderRadius: 4, transition: 'width 0.4s' }} />
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </SCard>

              </div>
            </>
          )}
        </>
      )}

      {/* ── Detail modal ─────────────────────────────────────────────────────── */}
      {detailSub && (
        <Modal title={`📋 ${detailSub.user_name} — 5S 評分詳情`} onClose={() => setDetailSub(null)}>
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 13, color: C.muted }}>週起始：{formatDate(detailSub.week_start)} · 提交：{formatDate(detailSub.submitted_at)}</div>
            <div style={{ background: scoreColor(detailSub.total_score) + '18', border: `2px solid ${scoreColor(detailSub.total_score)}`, borderRadius: 12, padding: '5px 14px', textAlign: 'center' }}>
              <span style={{ fontSize: 22, fontWeight: 900, color: scoreColor(detailSub.total_score) }}>{detailSub.total_score}</span>
              <span style={{ fontSize: 11, color: C.muted }}>/25</span>
              <div style={{ fontSize: 11, fontWeight: 700, color: scoreColor(detailSub.total_score) }}>{scoreLabel(detailSub.total_score)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <RadarChart scores={{ seiri: detailSub.seiri, seiton: detailSub.seiton, seiso: detailSub.seiso, seiketsu: detailSub.seiketsu, shitsuke: detailSub.shitsuke }} size={170} />
          </div>
          {FIVE_S.map(s => {
            const v = detailSub[s.key]
            const note = detailSub[s.key + '_note']
            return (
              <div key={s.key} style={{ marginBottom: 12, padding: '12px 14px', background: '#f8fafc', borderRadius: 12, borderLeft: `4px solid ${s.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: note ? 8 : 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{s.icon} {s.jp} {s.zh}</span>
                  <div style={{ background: SCORE_OPTIONS[v-1]?.bg, border: `1.5px solid ${dimColor(v)}`, borderRadius: 10, padding: '4px 12px', textAlign: 'center' }}>
                    <span style={{ fontSize: 18, fontWeight: 900, color: dimColor(v) }}>{v}</span>
                    <span style={{ fontSize: 10, color: dimColor(v), marginLeft: 4, fontWeight: 600 }}>{SCORE_OPTIONS[v-1]?.label}</span>
                  </div>
                </div>
                <DimBar score={v} color={s.color} />
                {note && <div style={{ fontSize: 12, color: C.sub, marginTop: 8, lineHeight: 1.6 }}>📝 {note}</div>}
              </div>
            )
          })}
        </Modal>
      )}
    </div>
  )
}
